import type { CustomerResponse } from '@monorepo/shared/schemas/customers'
import { Check, ChevronsUpDown, Loader2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { customerService } from '@/features/customers/services/customerService'

import { Button } from '@/shared/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/shared/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/ui/popover'
import { cn } from '@/shared/lib/utils'

interface CustomerAutocompleteProps {
  value: string
  onValueChange: (customerId: string, customer?: CustomerResponse) => void
  placeholder?: string
  disabled?: boolean
  className?: string
}

export function CustomerAutocomplete({
  value,
  onValueChange,
  placeholder = 'Search customers...',
  disabled = false,
  className,
}: CustomerAutocompleteProps) {
  const [open, setOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [results, setResults] = useState<CustomerResponse[]>([])
  const [loading, setLoading] = useState(false)
  const [selectedLabel, setSelectedLabel] = useState('')

  // Fetch selected customer's display label when value is provided externally
  useEffect(() => {
    if (!value) {
      setSelectedLabel('')
      return
    }
    customerService.getCustomerById(value).then(customer => {
      setSelectedLabel(customer.name)
    })
  }, [value])

  // Debounced API search
  useEffect(() => {
    if (!open) {
      return
    }
    const trimmed = searchTerm.trim()
    if (!trimmed) {
      setResults([])
      return
    }
    const timer = setTimeout(async () => {
      setLoading(true)
      try {
        const res = await customerService.searchCustomers({
          search: trimmed,
          limit: 20,
        })
        setResults(res.data || [])
      } catch {
        setResults([])
      } finally {
        setLoading(false)
      }
    }, 500)
    return () => clearTimeout(timer)
  }, [searchTerm, open])

  const handleOpenChange = (next: boolean) => {
    setOpen(next)
    if (!next) {
      setSearchTerm('')
    }
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant='outline'
          role='combobox'
          aria-expanded={open}
          disabled={disabled}
          className={cn('w-full justify-between font-normal', className)}
        >
          <span className='truncate'>
            {value && selectedLabel ? selectedLabel : placeholder}
          </span>
          <ChevronsUpDown className='ml-2 h-4 w-4 shrink-0 opacity-50' />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className='w-full p-0'
        style={{ width: 'var(--radix-popover-trigger-width)' }}
        align='start'
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder='Type to search...'
            value={searchTerm}
            onValueChange={setSearchTerm}
          />
          <CommandList>
            {loading && (
              <div className='flex items-center justify-center py-4'>
                <Loader2 className='h-4 w-4 animate-spin text-muted-foreground' />
              </div>
            )}
            {!loading && searchTerm.trim() && results.length === 0 && (
              <CommandEmpty>No customers found.</CommandEmpty>
            )}
            {!loading && !searchTerm.trim() && (
              <CommandEmpty>Type to search customers.</CommandEmpty>
            )}
            {results.length > 0 && (
              <CommandGroup>
                {results.map(customer => {
                  const subtitle =
                    (customer.metadata?.organization_name as
                      | string
                      | undefined) ||
                    customer.email ||
                    ''
                  return (
                    <CommandItem
                      key={customer.id}
                      value={customer.id}
                      onSelect={() => {
                        onValueChange(customer.id, customer)
                        setSelectedLabel(customer.name)
                        setOpen(false)
                      }}
                    >
                      <Check
                        className={cn(
                          'mr-2 h-4 w-4',
                          value === customer.id ? 'opacity-100' : 'opacity-0'
                        )}
                      />
                      <div className='flex flex-col'>
                        <span>{customer.name}</span>
                        {subtitle && (
                          <span className='text-xs text-muted-foreground'>
                            {subtitle}
                          </span>
                        )}
                      </div>
                    </CommandItem>
                  )
                })}
              </CommandGroup>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
